import { useEffect, useRef, useState } from 'react';

import { API_BASE } from './api';
import type { ActivityEventDto } from './dto';

/**
 * البث الحي لسجل الأثر (backend-api §11.2) — **محرك حيوية الواجهة** (§1.3).
 *
 * `activity_log` نفسه هو المجرى (قرار D49): كل حدث بعد الالتزام يُبث بـ
 * `event: activity` و`id: <ulid>` وحمولة السحب نفسها.
 *
 * ثلاث حقائق تُبنى عليها كل مستهلكات هذا الملف:
 *
 * 1. **العقد at-least-once** (قرار D49): الـ ULID يتولد قبل الالتزام، والاستئناف
 *    بـ `Last-Event-ID` يعيد نافذة تداخل 60 ثانية ⇒ **التكرار مضمون الحدوث**
 *    والمستهلك مسؤول عن إزالته بالـ `id`. لا ادعاء «تسليم مرة واحدة».
 * 2. **الاستئناف مجاني:** الخادم يرسل `id:` مع كل إطار، فالمتصفح يعيد إرسال
 *    `Last-Event-ID` عند إعادة الاتصال تلقائيًا — لا مؤشر يُخزَّن في العميل.
 * 3. **المصادقة بكوكي الجلسة وحدها** (قرار D47): الأصل واحد فلا ترويسة تفويض ولا
 *    token في الـ URL — و`EventSource` لا يستطيع حمل ترويسات أصلًا.
 *
 * `EventSource` يعيد الاتصال من نفسه بتراجع تلقائي، فلا حلقة إعادة محاولة هنا ولا
 * مؤقّت دوري (§5.6-4).
 */

export const ACTIVITY_STREAM_URL = `${API_BASE}/events/stream`;

/** اسم الحدث في الإطار كما يبثه الخادم (§11.2) — لا `onmessage` عام. */
export const ACTIVITY_EVENT_NAME = 'activity';

export type StreamStatus = 'connecting' | 'live' | 'stalled';

/**
 * تحقّق أدنى من الإطار: حمولة مشوّهة تُهمَل ولا تُسقط الشاشة. الحقول المفحوصة هي
 * ما تعتمد عليه الصفوف فعلًا؛ الباقي يُمرَّر كما جاء (نفس عقد `dto.ts`).
 */
export function parseActivityEvent(raw: unknown): ActivityEventDto | null {
  if (typeof raw !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null) return null;

  const candidate = parsed as Partial<ActivityEventDto>;
  if (typeof candidate.id !== 'string') return null;
  if (typeof candidate.verb !== 'string') return null;
  if (typeof candidate.entity_type !== 'string') return null;
  if (typeof candidate.summary_ar !== 'string') return null;
  if (typeof candidate.created_at !== 'string') return null;
  return candidate as ActivityEventDto;
}

/**
 * اتصال SSE واحد طوال حياة المكوّن المستضيف. `onEvent` يُقرأ من مرجع، فتغيّر الدالة
 * مع كل رسم **لا** يهدم الاتصال ويعيد بناءه (وهو ما يفقد الأحداث بين الهدم والبناء).
 */
export function useActivityStream(
  onEvent: (event: ActivityEventDto) => void,
  enabled = true,
): StreamStatus {
  const [status, setStatus] = useState<StreamStatus>('connecting');
  const handler = useRef(onEvent);

  useEffect(() => {
    handler.current = onEvent;
  }, [onEvent]);

  useEffect(() => {
    if (!enabled) {
      setStatus('stalled');
      return;
    }

    setStatus('connecting');
    const source = new EventSource(ACTIVITY_STREAM_URL, { withCredentials: true });

    const onOpen = (): void => setStatus('live');
    const onError = (): void => {
      // `CLOSED` = لن يعيد المتصفح المحاولة (رفض مصادقة مثلًا)؛ غيره إعادة اتصال جارية.
      setStatus(source.readyState === EventSource.CLOSED ? 'stalled' : 'connecting');
    };
    const onActivity = (event: Event): void => {
      if (!(event instanceof MessageEvent)) return;
      const parsed = parseActivityEvent(event.data);
      if (parsed !== null) handler.current(parsed);
    };

    source.addEventListener('open', onOpen);
    source.addEventListener('error', onError);
    source.addEventListener(ACTIVITY_EVENT_NAME, onActivity);

    return () => {
      source.removeEventListener('open', onOpen);
      source.removeEventListener('error', onError);
      source.removeEventListener(ACTIVITY_EVENT_NAME, onActivity);
      source.close();
    };
  }, [enabled]);

  return status;
}
